import { useState, useMemo, useEffect } from 'react';
import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import pluginsService from 'src/services/pluginsService';
import useAuth from 'src/hooks/useAuth';
import { PLUGINS_DESIGN as d } from './Plugins.design';

const CATEGORIES = [
    { id: 'all', label: 'Todos' },
    { id: 'recursos', label: 'Recursos' },
    { id: 'automacao', label: 'Automação' },
    { id: 'crm', label: 'CRM' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'mensagens', label: 'Mensagens' },
    { id: 'equipe', label: 'Equipe' },
    { id: 'outros', label: 'Outros' },
];

const getPlanBadge = (plan) => {
    const labels = { plus: 'Plus', essencial: 'Essencial', mini: 'Mini' };
    return { label: labels[plan] || plan, className: d.badge.plan[plan] || d.badge.plan.essencial };
};

const getCategoryLabel = (cat) => CATEGORIES.find(c => c.id === cat)?.label || cat;

// --------------- Card ---------------

function PluginCard({ plugin, onClick }) {
    const badge = getPlanBadge(plugin.plan);

    return (
        <div className={d.card.container} onClick={() => onClick(plugin)}>
            {plugin.installed && (
                <div className={d.card.installedRibbon}>
                    <span className={d.badge.installed}>
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Ativo
                    </span>
                </div>
            )}

            <div className={d.card.header}>
                <div className={`${d.card.iconBox} ${plugin.iconBg || 'bg-violet-50'}`}>
                    <Iconify icon={plugin.icon || 'lucide:puzzle'} width={24} className={plugin.iconColor || 'text-violet-600'} />
                </div>
                <div className={d.card.info}>
                    <div className={d.card.nameRow}>
                        <h3 className={d.card.name}>{plugin.name}</h3>
                        <span className={badge.className}>{badge.label}</span>
                    </div>
                    <p className={d.card.description}>{plugin.description}</p>
                </div>
            </div>

            <div className={d.card.footer}>
                <span className={d.card.price}>{plugin.price || 'Grátis'}</span>
                <span className={d.card.action}>
                    Ver detalhes
                    <Iconify icon="eva:arrow-forward-outline" width={14} />
                </span>
            </div>
        </div>
    );
}

// --------------- Modal ---------------

function PluginDetailModal({ plugin, onClose, onToggleInstall, installing }) {
    if (!plugin) return null;

    const badge = getPlanBadge(plugin.plan);
    const permissions = plugin.permissions || [];

    return (
        <>
            <div className={d.modal.backdrop} onClick={onClose} />
            <div className={d.modal.panel} onClick={onClose}>
                <div className={d.modal.container} onClick={(e) => e.stopPropagation()}>
                    <div className={d.modal.header.container}>
                        <div className={`${d.modal.header.iconBox} ${plugin.iconBg || 'bg-violet-50'}`}>
                            <Iconify icon={plugin.icon || 'lucide:puzzle'} width={32} className={plugin.iconColor || 'text-violet-600'} />
                        </div>
                        <div className={d.modal.header.info}>
                            <h2 className={d.modal.header.name}>{plugin.name}</h2>
                            <div className={d.modal.header.badgeRow}>
                                <span className={badge.className}>{badge.label}</span>
                                <span className="text-xs text-gray-400">·</span>
                                <span className="text-xs text-gray-500">{getCategoryLabel(plugin.category)}</span>
                                {plugin.installed && (
                                    <>
                                        <span className="text-xs text-gray-400">·</span>
                                        <span className={d.badge.installed}>
                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                            Ativo
                                        </span>
                                    </>
                                )}
                            </div>
                            <p className={d.modal.header.developer}>
                                por <span className="font-medium text-gray-700">{plugin.developer || 'LAIS'}</span>
                            </p>
                        </div>
                        <button onClick={onClose} className={d.modal.header.close}>
                            <Iconify icon="eva:close-outline" width={20} />
                        </button>
                    </div>

                    <div className={d.modal.body}>
                        <div className={d.modal.section.container}>
                            <h3 className={d.modal.section.title}>Sobre</h3>
                            <div className={d.modal.description}>
                                {plugin.longDescription || plugin.description}
                            </div>
                        </div>

                        {plugin.features && plugin.features.length > 0 && (
                            <div className={d.modal.section.container}>
                                <h3 className={d.modal.section.title}>Funcionalidades</h3>
                                <div className={d.modal.section.card}>
                                    <div className="space-y-1">
                                        {plugin.features.map((feat, i) => (
                                            <div key={i} className={d.modal.featureItem}>
                                                <Iconify icon="eva:checkmark-circle-2-fill" width={16} className="text-green-500 flex-shrink-0" />
                                                {feat}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {permissions.length > 0 && (
                            <div className={d.modal.section.container}>
                                <h3 className={d.modal.section.title}>Permissões necessárias</h3>
                                <div className={d.modal.section.card}>
                                    <div className={d.modal.permissionsGrid}>
                                        {permissions.map((perm, i) => (
                                            <div key={i} className={d.modal.permissionItem}>
                                                <Iconify icon="eva:shield-outline" width={14} className="text-gray-400 flex-shrink-0" />
                                                {perm}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={d.modal.footer.container}>
                        <div className={d.modal.footer.left}>
                            <span className="text-sm font-medium text-gray-600">{plugin.price || 'Grátis'}</span>
                        </div>
                        {plugin.installed ? (
                            <button
                                className={d.modal.footer.uninstallBtn}
                                onClick={() => onToggleInstall(plugin)}
                                disabled={installing}
                            >
                                <Iconify icon="eva:close-circle-outline" width={18} />
                                {installing ? 'Desinstalando...' : 'Desinstalar'}
                            </button>
                        ) : (
                            <button
                                className={d.modal.footer.installBtn}
                                onClick={() => onToggleInstall(plugin)}
                                disabled={installing}
                            >
                                <Iconify icon="eva:download-outline" width={18} />
                                {installing ? 'Instalando...' : 'Instalar'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

// --------------- Main ---------------

export default function PluginsComponent() {
    const { user } = useAuth();
    const [plugins, setPlugins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [installing, setInstalling] = useState(false);
    const [activeCategory, setActiveCategory] = useState('all');
    const [search, setSearch] = useState('');
    const [selectedPlugin, setSelectedPlugin] = useState(null);

    const clienteId = user?.clienteId;

    useEffect(() => {
        if (clienteId) loadPlugins();
    }, [clienteId]);

    const loadPlugins = async () => {
        try {
            setLoading(true);
            const response = await pluginsService.listarPorCliente(clienteId);
            if (response?.data) setPlugins(response.data);
        } catch (error) {
            console.error('Erro ao carregar plugins:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleInstall = async (plugin) => {
        try {
            setInstalling(true);
            if (plugin.installed) {
                await pluginsService.desinstalar(clienteId, plugin._id);
            } else {
                await pluginsService.instalar(clienteId, plugin._id);
            }

            const updated = p => p._id === plugin._id ? { ...p, installed: !p.installed } : p;
            setPlugins(prev => prev.map(updated));
            setSelectedPlugin(prev => prev?._id === plugin._id ? { ...prev, installed: !prev.installed } : prev);
        } catch (error) {
            console.error('Erro ao alterar plugin:', error);
        } finally {
            setInstalling(false);
        }
    };

    const filteredPlugins = useMemo(() => {
        let list = plugins;
        if (activeCategory !== 'all') list = list.filter(p => p.category === activeCategory);
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(p => p.name.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q));
        }
        return list;
    }, [plugins, activeCategory, search]);

    const getCategoryCount = (id) => id === 'all' ? plugins.length : plugins.filter(p => p.category === id).length;

    return (
        <div className={d.page.container}>
            {/* Header */}
            <div className={d.page.header.container}>
                <div className={d.page.header.left}>
                    <div className={d.page.header.iconBox}>
                        <Iconify icon="lucide:puzzle" width={24} className="text-violet-600" />
                    </div>
                    <div className={d.page.header.titleGroup}>
                        <h1 className={d.page.header.title}>Plugins</h1>
                        <p className={d.page.header.subtitle}>Gerencie os recursos instalados na sua conta</p>
                    </div>
                </div>
                <div className={d.page.searchWrapper}>
                    <Iconify icon="eva:search-outline" width={16} className={d.page.searchIcon} />
                    <Input
                        placeholder="Buscar plugins..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={d.page.searchInput}
                    />
                </div>
            </div>

            {/* Category Tabs */}
            <div className={d.tabs.container}>
                {CATEGORIES.filter(c => getCategoryCount(c.id) > 0 || c.id === 'all').map(cat => (
                    <button
                        key={cat.id}
                        onClick={() => setActiveCategory(cat.id)}
                        className={`${d.tabs.tab} ${activeCategory === cat.id ? d.tabs.tabActive : d.tabs.tabInactive}`}
                    >
                        {cat.label}
                        <span className="ml-1.5 opacity-70">({getCategoryCount(cat.id)})</span>
                    </button>
                ))}
            </div>

            {/* Grid */}
            {loading ? (
                <div className={d.loading.container}>
                    <Iconify icon="mdi:loading" width={32} className="animate-spin text-[#7C3AED] mb-3" />
                    <span className="text-sm text-gray-500">Carregando plugins...</span>
                </div>
            ) : filteredPlugins.length > 0 ? (
                <div className={d.grid}>
                    {filteredPlugins.map((plugin) => (
                        <PluginCard
                            key={plugin._id || plugin.slug}
                            plugin={plugin}
                            onClick={setSelectedPlugin}
                        />
                    ))}
                </div>
            ) : (
                <div className={d.emptyState.container}>
                    <div className={d.emptyState.iconBox}>
                        <Iconify icon="lucide:puzzle" width={36} className="text-violet-400" />
                    </div>
                    <h3 className={d.emptyState.title}>Nenhum plugin encontrado</h3>
                    <p className={d.emptyState.subtitle}>
                        Tente buscar com outros termos ou selecione outra categoria.
                    </p>
                </div>
            )}

            {/* Modal */}
            <PluginDetailModal
                plugin={selectedPlugin}
                onClose={() => setSelectedPlugin(null)}
                onToggleInstall={handleToggleInstall}
                installing={installing}
            />
        </div>
    );
}
